/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-loop-func */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Grid,
  Paper,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService, Coupon, CouponValidationResponse } from '../services/api';
import { notificationService } from '../utils/notificationService';
import { useAuth } from '../contexts/AuthContext';
import PaymentMethodSelector from '../components/PaymentMethodSelector';
import AddressModal from '../components/AddressModal';
import './CheckoutPage.css';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  brand?: string;
}

interface Address {
  id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  is_default: boolean;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  type: 'upi' | 'card' | 'wallet' | 'cod' | 'bnpl';
  description?: string;
  available?: boolean;
}

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  isGuest: boolean;
}

interface ShippingMethod {
  id: string;
  name: string;
  description: string;
  cost: number;
  estimatedDays: string;
  icon: string;
}

interface BillingInfo {
  name: string;
  street: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  sameAsShipping: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Order {
  id: string;
  user_id?: string;
  shop_id?: string;
  status?: string;
  total?: number;
  created_at?: string;
  [key: string]: any; // Allow additional properties
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Get cart items from location.state, with state management
  const locationState = location.state as any;
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    // Initialize from location.state if available
    if (locationState?.cartItems && Array.isArray(locationState.cartItems) && locationState.cartItems.length > 0) {
      console.log('CheckoutPage: Initializing cartItems from location.state:', locationState.cartItems);
      return locationState.cartItems;
    }
    return [];
  });
  
  const [totalAmount, setTotalAmount] = useState<number>(() => {
    return locationState?.totalAmount || 0;
  });

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasCheckedCart, setHasCheckedCart] = useState(false);
  
  // Customer Information
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: '',
    isGuest: true,
  });
  
  // Addresses
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  
  // Shipping Methods
  const [shippingMethods] = useState<ShippingMethod[]>([
    { id: 'standard', name: 'Standard Delivery', description: 'Regular delivery', cost: 50, estimatedDays: '3-5 days', icon: '🚚' },
    { id: 'express', name: 'Express Delivery', description: 'Faster delivery', cost: 100, estimatedDays: '1-2 days', icon: '⚡' },
    { id: 'same-day', name: 'Same Day Delivery', description: 'Delivery today', cost: 200, estimatedDays: 'Same day', icon: '🏃' },
  ]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<ShippingMethod | null>(null);
  
  // Billing Information
  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    name: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    sameAsShipping: true,
  });
  
  // Payment Methods
  const [paymentMethods] = useState<PaymentMethod[]>([
    { id: 'upi', name: 'UPI', icon: '💳', type: 'upi', description: 'Pay with UPI ID', available: true },
    { id: 'card', name: 'Credit/Debit Card', icon: '💳', type: 'card', description: 'Visa, Mastercard, RuPay', available: true },
    { id: 'apple-pay', name: 'Apple Pay', icon: '🍎', type: 'wallet', description: 'Pay with Apple Pay', available: true },
    { id: 'google-pay', name: 'Google Pay', icon: 'G', type: 'wallet', description: 'Pay with Google Pay', available: true },
    { id: 'paypal', name: 'PayPal', icon: 'P', type: 'wallet', description: 'Pay with PayPal', available: true },
    { id: 'klarna', name: 'Klarna (Pay Later)', icon: 'K', type: 'bnpl', description: 'Pay in 4 installments', available: true },
    { id: 'afterpay', name: 'Afterpay', icon: 'A', type: 'bnpl', description: 'Buy now, pay later', available: true },
    { id: 'cod', name: 'Cash on Delivery', icon: '💰', type: 'cod', description: 'Pay when delivered', available: true },
  ]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentDetails, setPaymentDetails] = useState<any>({});
  
  // Order Details
  const [orderNotes, setOrderNotes] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Loyalty Points
  const [availablePoints, setAvailablePoints] = useState(0);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);

  // Load loyalty points
  useEffect(() => {
    const loadPoints = () => {
      try {
        const userPoints = (user as any)?.loyalty_points ?? (user as any)?.points ?? 0;
        setAvailablePoints(userPoints);
      } catch (error) {
        console.error('Error loading loyalty points:', error);
      }
    };
    loadPoints();
  }, [user]);

  // Calculate points discount (10 points = ₹1)
  useEffect(() => {
    const discount = Math.floor(pointsToRedeem / 10);
    setPointsDiscount(discount);
  }, [pointsToRedeem]);

  // Calculate totals
  const shippingCost = selectedShippingMethod?.cost || 50;
  const taxAmount = (totalAmount - discountAmount - pointsDiscount) * 0.18; // 18% GST
  const finalTotal = Math.max(0, totalAmount - discountAmount - pointsDiscount + shippingCost + taxAmount);

  // Update cart items from location.state when it changes
  useEffect(() => {
    if (locationState?.cartItems && Array.isArray(locationState.cartItems) && locationState.cartItems.length > 0) {
      console.log('CheckoutPage: Updating cartItems from location.state:', locationState.cartItems);
      setCartItems(locationState.cartItems);
      setTotalAmount(locationState.totalAmount || 0);
      setHasCheckedCart(true);
    } else if (!hasCheckedCart) {
      // Only check localStorage if we haven't checked yet and location.state doesn't have items
      console.log('CheckoutPage: No items in location.state, checking localStorage');
      try {
        const multiShopCart = localStorage.getItem('multiShopCart');
        
        if (multiShopCart) {
          const multiCart = JSON.parse(multiShopCart);
          if (Array.isArray(multiCart) && multiCart.length > 0) {
            console.log('CheckoutPage: Found multiShopCart in localStorage, converting...');
            // Convert multi-shop cart to flat cart items
            const convertedItems: CartItem[] = [];
            let convertedTotal = 0;
            
            multiCart.forEach((shopCart: any) => {
              if (shopCart.items && Array.isArray(shopCart.items)) {
                shopCart.items.forEach((item: any) => {
                  const product = item.product || item;
                  convertedItems.push({
                    id: String(product.id || product.product_id),
                    name: product.name || product.product_name,
                    price: product.price || product.unit_price || 0,
                    quantity: item.quantity || 1,
                    image: product.image || product.product_image || '',
                    brand: product.brand || product.product_brand || ''
                  });
                  convertedTotal += (product.price || product.unit_price || 0) * (item.quantity || 1);
                });
              }
            });
            
            if (convertedItems.length > 0) {
              console.log('CheckoutPage: Using converted items from localStorage:', convertedItems);
              setCartItems(convertedItems);
              setTotalAmount(convertedTotal);
              setHasCheckedCart(true);
              return; // Don't redirect, we have items now
            }
          }
        }
      } catch (error) {
        console.error('Error reading cart from localStorage:', error);
      }
      
      setHasCheckedCart(true);
    }
  }, [locationState, hasCheckedCart]);

  // Load user addresses - only check cart after we've given location.state time to populate
  useEffect(() => {
    // Wait a moment to allow location.state to be set
    const checkCartTimer = setTimeout(() => {
      if (hasCheckedCart && (!cartItems || cartItems.length === 0)) {
        // Only redirect if we're absolutely sure there's no cart after checking everything
        console.warn('CheckoutPage: No cart items found anywhere, redirecting to cart');
        navigate('/app', { state: { view: 'cart' } });
        return;
      }
      
      // If we have cart items, load addresses
      if (cartItems && cartItems.length > 0) {
        console.log('CheckoutPage: Cart items available, loading addresses');
        loadAddresses();
      }
    }, 300); // Small delay to allow location.state to populate
    
    return () => clearTimeout(checkCartTimer);
  }, [cartItems, hasCheckedCart, navigate]);

  // Reload addresses when address modal closes (to get newly added addresses)
  useEffect(() => {
    if (!showAddressModal && isAuthenticated) {
      // Small delay to ensure AddressModal has finished saving
      const timer = setTimeout(() => {
        loadAddresses();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showAddressModal, isAuthenticated]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      if (!isAuthenticated) {
        console.log('🛒 CheckoutPage: User not authenticated, using empty addresses');
        setAddresses([]);
        return;
      }

      console.log('🛒 CheckoutPage: Loading addresses for authenticated user...');
      
      // Fetch real addresses from API
      const fetchedAddresses = await apiService.getUserAddresses();
      console.log('🛒 CheckoutPage: Fetched addresses:', fetchedAddresses);
      
      if (fetchedAddresses && Array.isArray(fetchedAddresses) && fetchedAddresses.length > 0) {
        // Convert API addresses to CheckoutPage Address format
        const convertedAddresses: Address[] = fetchedAddresses.map((addr: any) => ({
          id: addr.id,
          name: addr.name || 'Home',
          street: addr.address_line1 || addr.street || '',
          city: addr.city || '',
          state: addr.state || '',
          pincode: addr.postal_code || addr.pincode || '',
          phone: addr.phone || '',
          is_default: addr.is_default || false,
        }));
        console.log('🛒 CheckoutPage: Converted addresses:', convertedAddresses);
        setAddresses(convertedAddresses);
        
        // Set default address if available
        const defaultAddr = convertedAddresses.find(addr => addr.is_default) || convertedAddresses[0];
        if (defaultAddr && !selectedAddress) {
          console.log('🛒 CheckoutPage: Setting default address:', defaultAddr);
          setSelectedAddress(defaultAddr);
        }
      } else {
        // No addresses - use empty array (this is not an error)
        console.log('🛒 CheckoutPage: No addresses found, using empty array');
        setAddresses([]);
      }
    } catch (error: any) {
      console.error('❌ CheckoutPage: Error loading addresses:', error);
      console.error('❌ CheckoutPage: Error details:', {
        message: error?.message,
        status: error?.status,
        response: error?.response,
        stack: error?.stack
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load addresses';
      if (error?.message) {
        if (error.message.includes('Network') || error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Please log in to view your addresses.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Address service not available. Please try again later.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.message || errorMessage;
        }
      }
      
      setError(errorMessage);
      setAddresses([]); // Set empty array on error so user can still add addresses
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Step 0: Customer Information
    if (activeStep === 0) {
      if (!customerInfo.name || !customerInfo.email) {
        setError('Please fill in your name and email');
        return;
      }
    }
    
    // Step 1: Delivery Address
    if (activeStep === 1 && !selectedAddress) {
      setError('Please select a delivery address');
      return;
    }
    
    // Step 2: Shipping Method
    if (activeStep === 2 && !selectedShippingMethod) {
      setError('Please select a shipping method');
      return;
    }
    
    // Step 3: Billing Information
    if (activeStep === 3) {
      if (!billingInfo.sameAsShipping && (!billingInfo.name || !billingInfo.street)) {
        setError('Please fill in billing information or use same as shipping');
        return;
      }
    }
    
    // Step 4: Payment Method
    if (activeStep === 4 && !selectedPaymentMethod) {
      setError('Please select a payment method');
      return;
    }
    
    setError(null);
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleApplyCoupon = async () => {
    if (!discountCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    if (!isAuthenticated || !user) {
      setCouponError('Please log in to apply a coupon');
      return;
    }

    setValidatingCoupon(true);
    setCouponError(null);

    try {
      const validationRequest = {
        code: discountCode.trim(),
        user_id: user.id,
        order_amount: totalAmount,
        items: cartItems.map((item: CartItem) => ({
          product_id: item.id.toString(),
          quantity: item.quantity,
          price: item.price,
        })),
        shop_id: '0158d47b-70a4-48e5-a0e1-4575041cfb71', // Fresh Groceries Store
      };

      const response: CouponValidationResponse = await apiService.validateCoupon(validationRequest);

      if (response.valid && response.coupon) {
        setAppliedCoupon(response.coupon);
        setDiscountAmount(response.discount_amount);
        setCouponError(null);
        setSuccess('Coupon applied successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setCouponError(response.error || response.message || 'Invalid coupon code');
      }
    } catch (error: any) {
      console.error('Error validating coupon:', error);
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setCouponError(error.message || 'Failed to validate coupon. Please try again.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setDiscountCode('');
    setCouponError(null);
  };

  const handlePlaceOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        setError('Please log in to place an order');
        setLoading(false);
        return;
      }

      // Get shop orders from location.state or reconstruct from multiShopCart
      let shopOrders: Array<{ shop: any; items: CartItem[] }> = [];
      
      if (locationState?.shopOrders && Array.isArray(locationState.shopOrders)) {
        // Use shopOrders from location.state
        console.log('🛒 CheckoutPage: Using shopOrders from location.state:', locationState.shopOrders);
        shopOrders = locationState.shopOrders.map((so: any) => ({
          shop: so.shop,
          items: so.items.map((item: any) => ({
            id: item.product?.id || item.id,
            name: item.product?.name || item.name,
            price: item.product?.price || item.price,
            quantity: item.quantity,
            image: item.product?.image || item.image || '',
            brand: item.product?.brand || item.brand || ''
          }))
        }));
      } else {
        // Reconstruct shop orders from multiShopCart in localStorage
        console.log('🛒 CheckoutPage: Reconstructing shop orders from multiShopCart');
        try {
          const multiShopCart = localStorage.getItem('multiShopCart');
          if (multiShopCart) {
            const multiCart = JSON.parse(multiShopCart);
            if (Array.isArray(multiCart) && multiCart.length > 0) {
              // Check if nested format
              if (multiCart[0]?.shop && multiCart[0]?.items) {
                shopOrders = multiCart.map((shopCart: any) => ({
                  shop: shopCart.shop,
                  items: shopCart.items.map((item: any) => ({
                    id: item.product?.id || item.id,
                    name: item.product?.name || item.name,
                    price: item.product?.price || item.price,
                    quantity: item.quantity || 1,
                    image: item.product?.image || item.image || '',
                    brand: item.product?.brand || item.brand || ''
                  }))
                }));
              } else if (multiCart[0]?.product && multiCart[0]?.shop) {
                // Flat format - group by shop
                const shopMap = new Map<string, { shop: any; items: CartItem[] }>();
                multiCart.forEach((item: any) => {
                  const shopId = item.shop?.id || item.shop_id;
                  if (!shopMap.has(shopId)) {
                    shopMap.set(shopId, { shop: item.shop, items: [] });
                  }
                  shopMap.get(shopId)!.items.push({
                    id: item.product?.id || item.id,
                    name: item.product?.name || item.name,
                    price: item.product?.price || item.price,
                    quantity: item.quantity || 1,
                    image: item.product?.image || item.image || '',
                    brand: item.product?.brand || item.brand || ''
                  });
                });
                shopOrders = Array.from(shopMap.values());
              }
            }
          }
        } catch (e) {
          console.error('❌ CheckoutPage: Error reconstructing shop orders:', e);
        }
      }

      // If no shop orders found, create a single order with all items (fallback)
      if (shopOrders.length === 0) {
        console.warn('⚠️ CheckoutPage: No shop orders found, creating single order with all items');
        shopOrders = [{
          shop: { id: '0158d47b-70a4-48e5-a0e1-4575041cfb71', name: 'Default Shop' },
          items: cartItems
        }];
      }

      console.log('🛒 CheckoutPage: Processing', shopOrders.length, 'shop order(s):', shopOrders);

      // Create orders for each shop
      const createdOrders: Array<{ order: any; payment: any; shop: any }> = [];
      const failedOrders: Array<{ shop: any; error: string }> = [];

      for (const shopOrder of shopOrders) {
        try {
          // Validate shop order data
          if (!shopOrder.shop || !shopOrder.shop.id) {
            throw new Error(`Invalid shop data for order: ${JSON.stringify(shopOrder.shop)}`);
          }
          
          if (!shopOrder.items || shopOrder.items.length === 0) {
            throw new Error(`No items found for shop: ${shopOrder.shop.name}`);
          }

          // Calculate totals
          const subtotal = shopOrder.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          const shippingAmount = selectedShippingMethod?.cost || 0;
          const taxAmount = subtotal * 0.18; // 18% GST
          const shopTotal = subtotal + shippingAmount + taxAmount;

          // Create order payload for this shop
          const orderData = {
            user_id: user.id,
            shop_id: shopOrder.shop.id,
            items: shopOrder.items.map((item: CartItem) => {
              // Validate item data
              if (!item.id || !item.name || !item.price || !item.quantity) {
                console.error('❌ CheckoutPage: Invalid item data:', item);
                throw new Error(`Invalid item data: missing required fields`);
              }
              return {
                product_id: item.id.toString(),
                product_name: item.name,
                product_image: item.image || null,
                product_brand: item.brand || null,
                quantity: item.quantity,
                unit_price: item.price,
              };
            }),
            shipping_address_id: selectedAddress?.id || 'default-address',
            billing_address_id: selectedAddress?.id || 'default-address',
            payment_method: selectedPaymentMethod || 'cod',
            notes: orderNotes || `Order from ${shopOrder.shop.name}`,
            // Include calculated totals
            subtotal: subtotal,
            shipping_amount: shippingAmount,
            tax_amount: taxAmount,
            total_amount: shopTotal,
            // Include coupon data if applied (only for first shop or distribute as needed)
            ...(appliedCoupon && shopOrders.indexOf(shopOrder) === 0 && {
              coupon_code: appliedCoupon.code,
              coupon_id: appliedCoupon.id,
              discount_amount: (discountAmount + pointsDiscount) / shopOrders.length, // Distribute discount and points discount
              loyalty_points_redeemed: pointsToRedeem / shopOrders.length, // Distribute redeemed points
            }),
          };

          // Validate payload before sending
          if (!orderData.user_id) {
            throw new Error('Missing user_id in order payload');
          }
          if (!orderData.shop_id) {
            throw new Error('Missing shop_id in order payload');
          }
          if (!orderData.items || orderData.items.length === 0) {
            throw new Error('Missing or empty items array in order payload');
          }
          if (!orderData.shipping_address_id) {
            throw new Error('Missing shipping_address_id in order payload');
          }

          console.log(`🛒 CheckoutPage: Creating order for shop ${shopOrder.shop.name} (${shopOrder.shop.id}):`, orderData);
          
          // Create order using API
          let order: any;
          let orderResponse: any;
          try {
            orderResponse = await apiService.createOrder(orderData);
            console.log(`✅ CheckoutPage: Order creation response for ${shopOrder.shop.name}:`, orderResponse);
            console.log(`📋 CheckoutPage: Response structure:`, {
              has_order_id: !!orderResponse.order_id,
              has_id: !!orderResponse.id,
              has_orderId: !!orderResponse.orderId,
              has_order_number: !!orderResponse.order_number,
              all_keys: Object.keys(orderResponse),
              full_response: JSON.stringify(orderResponse, null, 2)
            });
            
            // Extract order ID from response
            const orderId = orderResponse.order_id || orderResponse.id || orderResponse.orderId || orderResponse.order_number;
            if (!orderId) {
              console.error(`❌ CheckoutPage: CRITICAL - No order ID found in response for ${shopOrder.shop.name}`);
              console.error(`❌ CheckoutPage: Full response:`, JSON.stringify(orderResponse, null, 2));
              console.error(`❌ CheckoutPage: This means the order was NOT created properly!`);
              throw new Error('Order creation failed: No order ID was returned in the response. The order was not saved to the database.');
            }
            
            console.log(`✅ CheckoutPage: Extracted order ID: ${orderId}`);
            
            // Ensure order object has all required fields
            order = {
              ...orderResponse,
              id: orderId,
              shop: shopOrder.shop,
              items: orderResponse.items || shopOrder.items.map((item: CartItem) => ({
                product_id: item.id.toString(),
                product_name: item.name,
                product_image: item.image || null,
                product_brand: item.brand || null,
                quantity: item.quantity,
                unit_price: item.price,
              })),
              subtotal: orderResponse.subtotal || shopTotal - (selectedShippingMethod?.cost || 0) - (shopTotal * 0.18),
              shipping_amount: orderResponse.shipping_amount || (selectedShippingMethod?.cost || 0),
              tax_amount: orderResponse.tax_amount || (shopTotal * 0.18),
              total_amount: orderResponse.total_amount || shopTotal,
              status: orderResponse.status || 'pending',
              created_at: orderResponse.created_at || new Date().toISOString(),
            };
            
            console.log(`✅ CheckoutPage: Order created successfully for ${shopOrder.shop.name} with ID:`, order.id);
            console.log(`✅ CheckoutPage: Order object structure:`, {
              id: order.id,
              shop_id: order.shop_id,
              user_id: order.user_id,
              items_count: order.items?.length,
              total_amount: order.total_amount,
              has_shop: !!order.shop,
              has_items: !!order.items,
              status: order.status
            });
            
            // Verify order was saved to database with retry logic
            let verifiedOrder = null;
            let verificationAttempts = 0;
            const maxVerificationAttempts = 5; // Increased attempts
            let verificationFailed = false;
            
            while (verificationAttempts < maxVerificationAttempts && !verifiedOrder) {
              try {
                verificationAttempts++;
                console.log(`🛒 CheckoutPage: Verifying order ${order.id} exists in database (attempt ${verificationAttempts}/${maxVerificationAttempts})...`);
                
                // Wait a bit before verification (order might need time to be saved)
                // Increased delay for later attempts
                await new Promise(resolve => setTimeout(resolve, 1000 * verificationAttempts));
                
                verifiedOrder = await apiService.getOrder(order.id);
                console.log(`✅ CheckoutPage: Order ${order.id} verified in database:`, verifiedOrder);
                
                // Update order with verified data if it has more complete information
                if (verifiedOrder && verifiedOrder.items) {
                  order = {
                    ...order,
                    ...verifiedOrder,
                    shop: order.shop, // Preserve shop info
                  };
                }
                break;
              } catch (verifyError: any) {
                console.warn(`⚠️ CheckoutPage: Verification attempt ${verificationAttempts} failed for order ${order.id}:`, verifyError?.message);
                if (verificationAttempts >= maxVerificationAttempts) {
                  console.error(`❌ CheckoutPage: CRITICAL - Could not verify order ${order.id} after ${maxVerificationAttempts} attempts.`);
                  console.error(`❌ CheckoutPage: Order was NOT saved to database!`);
                  console.error(`❌ CheckoutPage: Order creation response:`, orderResponse);
                  console.error(`❌ CheckoutPage: Extracted order ID:`, order.id);
                  verificationFailed = true;
                  
                  // Throw error to prevent adding unverified order to createdOrders
                  throw new Error(`Order ${order.id} was created but could not be verified in database. The order may not have been saved. Please check with support.`);
                }
              }
            }
            
            // Double-check: if we still don't have verified order, it's a problem
            if (!verifiedOrder && verificationFailed) {
              throw new Error(`Order ${order.id} verification failed. Order may not exist in database.`);
            }
          } catch (orderError: any) {
            console.error(`❌ CheckoutPage: Order creation failed for ${shopOrder.shop.name}:`, orderError);
            failedOrders.push({
              shop: shopOrder.shop,
              error: orderError?.message || 'Failed to create order'
            });
            continue; // Continue with other shops
          }
          
          // Create separate payment for this order
          let payment = null;
          try {
            const paymentData = {
              order_id: order.id,
              amount: shopTotal,
              currency: "INR",
              payment_method: selectedPaymentMethod,
              payment_provider: "mock",
              description: `Payment for order ${order.id} from ${shopOrder.shop.name}`,
            };

            console.log(`💳 CheckoutPage: Creating payment for order ${order.id}:`, paymentData);
            payment = await apiService.createPayment(paymentData);
            console.log(`✅ CheckoutPage: Payment created successfully for order ${order.id}:`, payment);
          } catch (paymentError: any) {
            console.warn(`⚠️ CheckoutPage: Payment creation failed for order ${order.id}, but order was created:`, paymentError);
            // Continue - order was created even if payment failed
          }
          
          createdOrders.push({ order, payment, shop: shopOrder.shop });
          
        } catch (error: any) {
          console.error(`❌ CheckoutPage: Error processing order for ${shopOrder.shop.name}:`, error);
          failedOrders.push({
            shop: shopOrder.shop,
            error: error?.message || 'Unknown error'
          });
          // Continue with other shops
        }
      }

      // Show results
      if (createdOrders.length === 0) {
        setError('Failed to create any orders. Please try again.');
        setLoading(false);
        return;
      }

      if (failedOrders.length > 0) {
        const failedShops = failedOrders.map(f => f.shop.name).join(', ');
        setError(`Some orders failed: ${failedShops}. ${createdOrders.length} order(s) were created successfully.`);
      } else {
        const orderIds = createdOrders.map(co => co.order.id).join(', ');
        setSuccess(`${createdOrders.length} order(s) placed successfully! Order IDs: ${orderIds}`);
      }
      
      // Handle loyalty points: Redeem and Earn
      if (pointsToRedeem > 0) {
        try {
          const newPoints = availablePoints - pointsToRedeem;
          // Update user points (in real app, this would be an API call)
          const updatedTransactions = JSON.parse(localStorage.getItem('loyalty_transactions') || '[]');
          updatedTransactions.push({
            id: Date.now().toString(),
            type: 'redeemed',
            amount: pointsToRedeem,
            description: `Order #${createdOrders.map(co => co.order.id).join(', ')}`,
            date: new Date().toISOString()
          });
          localStorage.setItem('loyalty_transactions', JSON.stringify(updatedTransactions));
          console.log(`✅ CheckoutPage: Redeemed ${pointsToRedeem} loyalty points`);
        } catch (error) {
          console.error('Error redeeming loyalty points:', error);
        }
      }

      // Earn loyalty points (1 point per ₹10 spent)
      try {
        const pointsEarned = Math.floor(finalTotal / 10);
        if (pointsEarned > 0) {
          const newPoints = (availablePoints - pointsToRedeem) + pointsEarned;
          const updatedTransactions = JSON.parse(localStorage.getItem('loyalty_transactions') || '[]');
          updatedTransactions.push({
            id: (Date.now() + 1).toString(),
            type: 'earned',
            amount: pointsEarned,
            description: `Order #${createdOrders.map(co => co.order.id).join(', ')}`,
            date: new Date().toISOString()
          });
          localStorage.setItem('loyalty_transactions', JSON.stringify(updatedTransactions));
          console.log(`✅ CheckoutPage: Earned ${pointsEarned} loyalty points`);
        }
      } catch (error) {
        console.error('Error earning loyalty points:', error);
      }

      // Clear cart from localStorage
      try {
        localStorage.removeItem('cart');
        localStorage.removeItem('multiShopCart');
        console.log('✅ CheckoutPage: Cart cleared from localStorage');
      } catch (clearError) {
        console.warn('⚠️ CheckoutPage: Failed to clear cart from localStorage:', clearError);
      }

      // Create notifications for successful orders
      try {
        if (createdOrders.length > 0 && user?.id) {
          const firstOrder = createdOrders[0].order;
          const orderNumber = firstOrder.order_number || firstOrder.id;
          const totalAmount = finalTotal;
          const userId = user.id;
          
          console.log('🔔 CheckoutPage: Creating notifications for user:', userId);
          
          // Create notification for order placed
          await notificationService.notifyOrderPlaced(
            firstOrder.id,
            orderNumber,
            totalAmount,
            createdOrders.length,
            userId
          );

          // Create notification for payment if payment was successful
          if (createdOrders[0].payment) {
            const payment = createdOrders[0].payment;
            const paymentMethod = firstOrder.payment_method || selectedPaymentMethod || 'Cash on Delivery';
            await notificationService.notifyPaymentReceived(
              firstOrder.id,
              orderNumber,
              totalAmount,
              paymentMethod,
              userId
            );
          }
        } else {
          console.warn('⚠️ CheckoutPage: Cannot create notifications - no user ID or no orders');
        }
      } catch (notifError) {
        console.warn('Failed to create order notifications:', notifError);
        // Don't block the flow if notification creation fails
      }
      
      // Redirect to order confirmation page with all orders
      setTimeout(() => {
        const firstOrderId = createdOrders[0].order.id;
        navigate(`/order-confirmation/${firstOrderId}`, { 
          state: { 
            orders: createdOrders.map(co => co.order), // All orders
            order: createdOrders[0].order, // First order for compatibility
            payments: createdOrders.map(co => co.payment).filter(p => p !== null),
            failedOrders: failedOrders.length > 0 ? failedOrders : undefined,
            pointsEarned: Math.floor(finalTotal / 10),
            pointsRedeemed: pointsToRedeem
          } 
        });
      }, 2000);

    } catch (error) {
      console.error('❌ CheckoutPage: Error placing order:', error);
      setError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Customer Info', 'Delivery Address', 'Shipping Method', 'Billing Info', 'Payment Method', 'Review Order'];

  const renderCustomerInfoStep = () => (
    <Card className="checkout-card">
      <CardContent>
        <Typography className="checkout-section-title">
          Customer Information
        </Typography>
        
        {/* Guest Checkout Option */}
        <Box display="flex" gap={1} mb={3}>
          <Button
            className={customerInfo.isGuest ? 'checkout-button-primary' : 'checkout-button-outlined'}
            onClick={() => setCustomerInfo({ ...customerInfo, isGuest: true })}
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            Continue as Guest
          </Button>
          <Button
            className={!customerInfo.isGuest ? 'checkout-button-primary' : 'checkout-button-outlined'}
            onClick={() => setCustomerInfo({ ...customerInfo, isGuest: false })}
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            Login / Create Account
          </Button>
        </Box>

        {/* Customer Information Form */}
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            label="Full Name *"
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
            required
            className="checkout-textfield"
          />
          
          <TextField
            fullWidth
            label="Email Address *"
            type="email"
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
            required
            className="checkout-textfield"
          />
          
          <TextField
            fullWidth
            label="Phone Number (Optional)"
            type="tel"
            value={customerInfo.phone}
            onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
            className="checkout-textfield"
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderAddressStep = () => (
    <Card className="checkout-card">
      <CardContent>
        <Typography className="checkout-section-title">
          Select Delivery Address
        </Typography>
        
        {addresses.map((address) => (
          <div
            key={address.id}
            className={`checkout-address-card ${selectedAddress?.id === address.id ? 'selected' : ''}`}
            onClick={() => setSelectedAddress(address)}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography className="checkout-address-name">
                {address.name}
              </Typography>
              {address.is_default && (
                <Typography sx={{ fontSize: '12px', color: '#2563eb', fontWeight: 700 }}>
                  DEFAULT
                </Typography>
              )}
            </Box>
            <Typography className="checkout-address-text">
              {address.street}
            </Typography>
            <Typography className="checkout-address-text">
              {address.city}, {address.state} - {address.pincode}
            </Typography>
            <Typography className="checkout-address-text">
              {address.phone}
            </Typography>
          </div>
        ))}

        <Button
          className="checkout-button-outlined"
          startIcon={<span>+</span>}
          sx={{ mt: 2, textTransform: 'none' }}
          fullWidth
          onClick={() => setShowAddressModal(true)}
        >
          Add New Address
        </Button>
      </CardContent>
    </Card>
  );

  const renderShippingMethodStep = () => (
    <Card className="checkout-card">
      <CardContent>
        <Typography className="checkout-section-title">
          Select Shipping Method
        </Typography>
        
        {shippingMethods.map((method) => (
          <div
            key={method.id}
            className={`checkout-shipping-card ${selectedShippingMethod?.id === method.id ? 'selected' : ''}`}
            onClick={() => setSelectedShippingMethod(method)}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                  {method.icon}
                </Typography>
                <Box>
                  <Typography className="checkout-shipping-name">
                    {method.name}
                  </Typography>
                  <Typography className="checkout-shipping-description">
                    {method.description}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#2563eb', marginTop: '4px' }}>
                    Est. {method.estimatedDays}
                  </Typography>
                </Box>
              </Box>
              <Typography className="checkout-shipping-price">
                ₹{method.cost}
              </Typography>
            </Box>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderBillingStep = () => (
    <Card className="checkout-card">
      <CardContent>
        <Typography className="checkout-section-title">
          Billing Information
        </Typography>
        
        {/* Same as Shipping Option */}
        <Box display="flex" alignItems="center" mb={2}>
          <input
            type="checkbox"
            checked={billingInfo.sameAsShipping}
            onChange={(e) => setBillingInfo({ ...billingInfo, sameAsShipping: e.target.checked })}
            style={{ marginRight: 8, width: '18px', height: '18px', accentColor: '#2563eb' }}
          />
          <Typography sx={{ fontSize: '16px', color: '#000000', fontFamily: 'Poppins, Roboto, sans-serif' }}>
            Same as shipping address
          </Typography>
        </Box>

        {!billingInfo.sameAsShipping && (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Billing Name *"
              value={billingInfo.name}
              onChange={(e) => setBillingInfo({ ...billingInfo, name: e.target.value })}
              required
              className="checkout-textfield"
            />
            
            <TextField
              fullWidth
              label="Street Address *"
              multiline
              rows={2}
              value={billingInfo.street}
              onChange={(e) => setBillingInfo({ ...billingInfo, street: e.target.value })}
              required
              className="checkout-textfield"
            />
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="City *"
                value={billingInfo.city}
                onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })}
                required
                className="checkout-textfield"
              />
              <TextField
                fullWidth
                label="State *"
                value={billingInfo.state}
                onChange={(e) => setBillingInfo({ ...billingInfo, state: e.target.value })}
                required
                className="checkout-textfield"
              />
            </Box>
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Pincode *"
                value={billingInfo.pincode}
                onChange={(e) => setBillingInfo({ ...billingInfo, pincode: e.target.value })}
                required
                className="checkout-textfield"
              />
              <TextField
                fullWidth
                label="Phone"
                value={billingInfo.phone}
                onChange={(e) => setBillingInfo({ ...billingInfo, phone: e.target.value })}
                className="checkout-textfield"
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderPaymentStep = () => (
    <PaymentMethodSelector
      selectedMethod={selectedPaymentMethod}
      onMethodSelect={(methodId: string) => {
        setSelectedPaymentMethod(methodId);
      }}
      onPaymentDetails={(details: any) => {
        setSelectedPaymentMethod(details.method.id);
        setPaymentDetails(details.details);
        // Auto-advance to next step when payment method is confirmed
        if (activeStep < 5) {
          setActiveStep(activeStep + 1);
        }
      }}
      totalAmount={totalAmount}
    />
  );

  const renderReviewStep = () => (
    <Grid container spacing={3}>
      {/* Order Items */}
      <Grid item xs={12} md={8}>
        {/* Customer Information */}
        <Card className="checkout-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Customer Information
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000000', marginBottom: '8px' }}>
                {customerInfo.name}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>
                {customerInfo.email}
              </Typography>
              {customerInfo.phone && (
                <Typography sx={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>
                  {customerInfo.phone}
                </Typography>
              )}
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>
                {customerInfo.isGuest ? 'Guest Checkout' : 'Registered Customer'}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card className="checkout-card">
          <CardContent>
            <Typography className="checkout-section-title">
              Order Items
            </Typography>
            <List>
              {cartItems.map((item: CartItem) => (
                <ListItem key={item.id} divider sx={{ padding: '16px 0' }}>
                  <ListItemAvatar>
                    <Avatar sx={{ fontSize: '1.5rem', width: 60, height: 60, bgcolor: '#f9fafb' }}>
                      {item.image ? (
                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      ) : (
                        item.name.charAt(0)
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000' }}>{item.name}</Typography>}
                    secondary={
                      <Box>
                        {item.brand && (
                          <Typography sx={{ fontSize: '14px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                            {item.brand}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '14px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                          ₹{item.price} × {item.quantity}
                        </Typography>
                      </Box>
                    }
                  />
                  <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#2563eb' }}>
                    ₹{(item.price * item.quantity).toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Delivery Address */}
        <Card className="checkout-card" sx={{ mt: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Delivery Address
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000000', marginBottom: '8px' }}>
                {selectedAddress?.name}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>
                {selectedAddress?.street}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>
                {selectedAddress?.city}, {selectedAddress?.state} - {selectedAddress?.pincode}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>
                {selectedAddress?.phone}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Shipping Method */}
        <Card className="checkout-card" sx={{ mt: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Shipping Method
            </Typography>
            <Box display="flex" alignItems="center" sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                {selectedShippingMethod?.icon}
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000000', marginBottom: '4px' }}>
                  {selectedShippingMethod?.name}
                </Typography>
                <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>
                  Est. {selectedShippingMethod?.estimatedDays}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#2563eb', ml: 'auto' }}>
                ₹{selectedShippingMethod?.cost}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Billing Information */}
        <Card className="checkout-card" sx={{ mt: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Billing Information
            </Typography>
            {billingInfo.sameAsShipping ? (
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>
                Same as shipping address
              </Typography>
            ) : (
              <Box sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000000', marginBottom: '8px' }}>
                  {billingInfo.name}
                </Typography>
                <Typography sx={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>
                  {billingInfo.street}
                </Typography>
                <Typography sx={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>
                  {billingInfo.city}, {billingInfo.state} - {billingInfo.pincode}
                </Typography>
                {billingInfo.phone && (
                  <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>
                    {billingInfo.phone}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card className="checkout-card" sx={{ mt: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Payment Method
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000000' }}>
                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Order Notes */}
        <Card className="checkout-card" sx={{ mt: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Order Notes (Optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Any special instructions for delivery..."
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              className="checkout-textfield"
            />
          </CardContent>
        </Card>

        {/* Loyalty Points Redemption */}
        {availablePoints > 0 && (
          <Card className="checkout-card" sx={{ mt: 2 }}>
            <CardContent>
              <Typography className="checkout-section-title">
                Loyalty Points
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 1 }}>
                  Available: {availablePoints} points (₹{Math.floor(availablePoints / 10)} discount)
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Points to redeem"
                    value={pointsToRedeem || ''}
                    onChange={(e) => {
                      const value = Math.max(0, Math.min(availablePoints, parseInt(e.target.value) || 0));
                      setPointsToRedeem(value);
                    }}
                    fullWidth
                    className="checkout-textfield"
                    inputProps={{ min: 0, max: availablePoints }}
                  />
                  <Button
                    className="checkout-button-outlined"
                    size="small"
                    onClick={() => setPointsToRedeem(0)}
                    disabled={pointsToRedeem === 0}
                    sx={{ textTransform: 'none', minWidth: '80px' }}
                  >
                    Clear
                  </Button>
                </Box>
                {pointsDiscount > 0 && (
                  <Typography sx={{ fontSize: '14px', color: '#10b981', mt: 1, fontWeight: 600 }}>
                    Discount: -₹{pointsDiscount.toFixed(2)}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: 1 }}>
                  10 points = ₹1 discount
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Discount Code */}
        <Card className="checkout-card" sx={{ mt: 2 }}>
          <CardContent>
            <Typography className="checkout-section-title">
              Discount Code
            </Typography>
            {appliedCoupon ? (
              <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>
                    ✓ {appliedCoupon.code} Applied
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleRemoveCoupon}
                    sx={{ textTransform: 'none', color: '#dc2626', minWidth: 'auto', padding: '4px 8px' }}
                  >
                    Remove
                  </Button>
                </Box>
                {appliedCoupon.description && (
                  <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 1 }}>
                    {appliedCoupon.description}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#10b981' }}>
                  Discount: -₹{discountAmount.toFixed(2)}
                </Typography>
              </Box>
            ) : (
              <>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    placeholder="Enter code"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value);
                      setCouponError(null);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleApplyCoupon();
                      }
                    }}
                    fullWidth
                    className="checkout-textfield"
                    disabled={validatingCoupon}
                    error={!!couponError}
                  />
                  <Button
                    className="checkout-button-outlined"
                    size="small"
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !discountCode.trim()}
                    sx={{ textTransform: 'none' }}
                  >
                    {validatingCoupon ? <CircularProgress size={16} /> : 'Apply'}
                  </Button>
                </Box>
                {couponError && (
                  <Typography sx={{ fontSize: '14px', color: '#dc2626', marginTop: '8px' }}>
                    {couponError}
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Order Summary */}
      <Grid item xs={12} md={4}>
        <Paper className="checkout-order-summary">
          <Typography className="checkout-section-title" sx={{ fontSize: '24px', marginBottom: '16px' }}>
            Order Summary
          </Typography>
          <Divider sx={{ my: 2, borderColor: '#e5e7eb' }} />
          
            <Box className="checkout-order-total-line">
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>Subtotal</Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>₹{totalAmount.toFixed(2)}</Typography>
            </Box>
            
            {discountAmount > 0 && (
              <Box className="checkout-order-total-line">
                <Typography sx={{ fontSize: '16px', color: '#10b981' }}>Discount</Typography>
                <Typography sx={{ fontSize: '16px', color: '#10b981' }}>-₹{discountAmount.toFixed(2)}</Typography>
              </Box>
            )}
            
            {pointsDiscount > 0 && (
              <Box className="checkout-order-total-line">
                <Typography sx={{ fontSize: '16px', color: '#10b981' }}>Loyalty Points Discount</Typography>
                <Typography sx={{ fontSize: '16px', color: '#10b981' }}>-₹{pointsDiscount.toFixed(2)}</Typography>
              </Box>
            )}
            
            <Box className="checkout-order-total-line">
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>Shipping</Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>₹{shippingCost.toFixed(2)}</Typography>
            </Box>
            
            <Box className="checkout-order-total-line">
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>Tax (18%)</Typography>
              <Typography sx={{ fontSize: '16px', color: '#6b7280' }}>₹{taxAmount.toFixed(2)}</Typography>
            </Box>
          
          <Divider sx={{ my: 2, borderColor: '#e5e7eb' }} />
          
          <Box className="checkout-order-total-line grand-total">
            <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#000000' }}>
              Total
            </Typography>
            <Typography className="checkout-total-amount" sx={{ fontSize: '24px', fontWeight: 700 }}>
              ₹{finalTotal.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderCustomerInfoStep();
      case 1:
        return renderAddressStep();
      case 2:
        return renderShippingMethodStep();
      case 3:
        return renderBillingStep();
      case 4:
        return renderPaymentStep();
      case 5:
        return renderReviewStep();
      default:
        return null;
    }
  };

  if (loading && activeStep === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <CircularProgress sx={{ color: '#2563eb' }} />
            <Typography className="checkout-loading-text">
              Loading checkout...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <Typography className="checkout-title">
          Checkout
        </Typography>

        <Stepper activeStep={activeStep} className="checkout-stepper">
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" className="checkout-alert" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="checkout-alert">
            {success}
          </Alert>
        )}

        {renderStepContent()}

        <Box className="checkout-navigation">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className="checkout-button-outlined"
          >
            Back
          </Button>

          <Button
            onClick={activeStep === steps.length - 1 ? handlePlaceOrder : handleNext}
            disabled={loading}
            className="checkout-button-primary"
            startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
          >
            {loading
              ? 'Processing...'
              : activeStep === steps.length - 1
              ? 'Place Order'
              : 'Next'}
          </Button>
        </Box>
      </div>

      {/* Address Modal */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={async () => {
          setShowAddressModal(false);
          // Reload addresses when modal closes to get any newly added addresses
          await loadAddresses();
        }}
        onSelectAddress={async (formattedAddress) => {
          setShowAddressModal(false);
          // Reload addresses to get the latest list (including newly added)
          await loadAddresses();
          // Find the matching address after a short delay to ensure state is updated
          setTimeout(() => {
            // Use a function to get the latest addresses state
            loadAddresses().then(() => {
              // The addresses will be updated in state, and we'll find the match
              // This will be handled by the address list re-render
            });
          }, 200);
        }}
        currentAddress={selectedAddress ? `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pincode}` : undefined}
      />
    </div>
  );
};

export default CheckoutPage;
